import React from "react";
import { MAX_ELLIPSIS_LENGTH } from "../../../js/common/base/constants";

const ALLOWED_RENDER_TAGS = new Set(['ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'p', 'br', 'span', 'div', 'a']);

function isSafeUrl(href) {
    if (!href || typeof href !== 'string') return false;
    if (href.startsWith('/') || href.startsWith('#') || href.startsWith('.')) return true;
    try {
        const parsed = new URL(href);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:' || parsed.protocol === 'mailto:';
    } catch (e) {
        return false;
    }
}

function domNodeToReact(node, key = 0) {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
    }
    const tag = node.tagName.toLowerCase();
    if (!ALLOWED_RENDER_TAGS.has(tag)) {
        return null;
    }
    const children = Array.from(node.childNodes).map((child, i) => domNodeToReact(child, i));
    const props = { key };
    if (tag === 'a') {
        const href = node.getAttribute('href') || '';
        if (isSafeUrl(href)) {
            props.href = href;
            props.rel = 'noopener noreferrer';
            const target = node.getAttribute('target');
            if (target && target !== '_self') props.target = target;
            const style = node.getAttribute('style');
            if (style) props.style = { cssText: style };
        } else {
            return React.createElement(React.Fragment, { key }, ...children);
        }
    } else {
        const style = node.getAttribute('style');
        if (style) props.style = { cssText: style };
        const cls = node.getAttribute('class');
        if (cls) props.className = cls;
    }
    return React.createElement(tag, props, ...children);
}

function htmlToReact(html) {
    if (!html || typeof html !== 'string') return null;
    try {
        const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
        const root = doc.body.firstChild;
        if (!root) return null;
        return Array.from(root.childNodes).map((child, i) => domNodeToReact(child, i));
    } catch (e) {
        return String(html);
    }
}

export default class FeatureSectionCtrl extends React.Component {
    getSafeUrl(rawUrl) {
        if (typeof rawUrl !== 'string' || !rawUrl.trim()) {
            return null;
        }

        if (rawUrl.startsWith('/')) {
            return rawUrl;
        }

        if (rawUrl.startsWith('#')) {
            return rawUrl;
        }

        try {
            const parsed = new URL(rawUrl, window.location.origin);
            if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:' && parsed.protocol !== 'mailto:' && parsed.protocol !== 'tel:') {
                return null;
            }
            return parsed.toString();
        } catch (e) {
            return null;
        }
    }

    render() {
        const data = this.props.data;
        const header = this.renderHeader(data.header);
        const cards = this.renderCards(data.cards, data.section.containerClass);
        const footer = this.renderFooter(data.footer);

        const className = 'pubhub-feature-section section-padding-sm';

        return (
            <div id={data.section.id} className={className} style={data.section.style}>
                {header}
                {cards}
                {footer}
            </div>
        );
    }
    renderHeader(data) {
        if (!data) {
            return;
        }

        const icon = this.renderImage(data.icon, 'pubhub-feature-section-header');
        const title = this.renderText(data.title, 'pubhub-feature-section-header-title');
        const description = this.renderText(data.description, 'pubhub-feature-section-header-desc');
        const action = this.renderText(data.action, 'pubhub-feature-section-header-action');
        const className = ('pubhub-feature-section-header ' + (data.className || '')).trim();
        const containerClass = data.containerClass ? data.containerClass : 'container';
        if(!title && !description) {
            return null;
        }
        return (
            <div className={className} style={data.style}>
                <div className={containerClass}>
                    {icon}
                    {title}
                    {description}
                    {action}
                </div>
            </div>
        );
    }
    renderFooter(data) {
        if (!data || data.actions.length == 0) {
            return;
        }

        const actions = data.actions.map((action, idx) => this.renderText(action, 'pubhub-feature-section-footer-action', idx));
        const className = ('pubhub-feature-section-footer container ' + (data.className || '')).trim();
        return (
            <div className={className} style={data.style}>
                {actions}
            </div>
        );
    }
    renderCards(data, containerClass) {
        if (!data || data.length == 0) {
            return null;
        }

        const cards = data.map((card, idx) => {
            return this.renderCard(card, idx);
        });

        return (
            <div className={`pubhub-feature-section-body ${containerClass ? containerClass : 'container'}`}>
                <div className='row'>
                    {cards}
                </div>
            </div>
        );
    }
    renderCard(card, key) {
        const cols = this.getCardColumnClassName(card);
        const icon = this.renderImage(card.icon, 'pubhub-feature-section-card');
        const iconTitle = this.renderText(card.iconTitle, 'pubhub-feature-section-card-icon-title');
        const title = this.renderText(card.title, '');
        const description = this.renderText(card.description, 'pubhub-feature-section-card-desc');
        const items = this.rendeCardItems(card.items);
        const flexGap = this.renderCardActionFlexGap(card.action);
        const action = this.renderText(card.action, 'pubhub-feature-section-card-action');
        
        let containerCols = cols;
        let cardClassName = card.className || '';
        if (card.action.href === "#/" || card.action.href === "/auth/modern-commerce/") {
            containerCols = containerCols.replace(/\bhide\b/g, '').trim();
            cardClassName = cardClassName.replace(/\bhide\b/g, '').trim();
        }
        
        const containerClassName = (`pubhub-feature-section-card-container ${containerCols}`).trim()
        const className = (`pubhub-feature-section-card ${cardClassName}`).trim();
        const iconPartClassName = (card.icon?.src ? ' has-icon' : '');
        const cardSHeaderStyle = card.icon ? card.icon.style : {};
        // const cardHref = card.action.href === "#/" ? "/auth/modern-commerce/" : card.action.href;
        const containerStyle = (card.action.href === "#/" || card.action.href === "/auth/modern-commerce/") 
            ? { ...card.style, display: 'block !important' } 
            : card.style;
        const containerRef = (card.action.href === "#/" || card.action.href === "/auth/modern-commerce/") 
            ? (el) => { if (el) el.style.setProperty('display', 'block', 'important'); }
            : null;
        return (
            <a href={card.action.href} key={key} id={card.id} className={containerClassName} style={card.style} ref={containerRef} >
                <div className={className} style={card.style}>
                    <div className={"card-header" + iconPartClassName} style={cardSHeaderStyle}>
                        <h2 className="card-title">{title}</h2>
                    </div>
                    <div className="card-body d-flex flex-column justify-content-between">
                        <div className="card-description">
                            {description}
                            {items}
                            {flexGap}
                        </div>
                        {/* <div>{action}</div> */}
                    </div>
                </div>
            </a>
            // <div key={key} id={card.id} className={containerClassName} style={card.style}>
            //     {/* <div className={className} style={card.style}>
            //          <div className={'pubhub-feature-section-card-iconpart' + iconPartClassName}>
            //             {iconTitle}
            //             {icon}
            //         </div> 
            //         <div className='pubhub-feature-section-card-textpart'>
            //             {title}
            //             {description}
            //             {items}
            //             {flexGap}
            //             {action}
            //         </div>
            //     </div> */}
            //     <div className={className} style={card.style}>
            //         <div class="card-header" style={{backgroundImage: `url(${card.icon.src})` }}>
            //             <h2 class="card-title">{title}</h2>
            //         </div>
            //         <div class="card-body d-flex flex-column justify-content-between">
            //             <div class="card-description">
            //                 {description}
            //                 {items}
            //                 {flexGap}
            //             </div>
            //             <div>{action}</div>
            //         </div>
            //     </div>
            // </div>
        );
    }

    renderText(data, classNameFixedPart = '', key) {
        if (!data) {
            return null;
        }

        const { text, href, target, id, className, style } = data;
        const safeHref = this.getSafeUrl(href);
        let smartTarget = this.getLinkTarget(safeHref, target);
        style.borderColor = style.borderColor || style.backgroundColor;

        const colorTextClassName = style.color ? 'type-colorful' : '';
        const fullClassName = (`${classNameFixedPart} ${className} ${colorTextClassName}`).trim();
        let displayText = text.trim();

        const urlRegex = /(https?:\/\/[^\s<]+)/g;
        const urls = displayText.match(urlRegex);
        const hasMultipleUrls = urls && urls.length > 1;
        
        // Convert URLs to clickable links FIRST
        displayText = this.linkifyText(displayText);
        
        // Then truncate based on visible text length (strip HTML tags)
        if(classNameFixedPart == 'pubhub-feature-section-card-desc'){
            const visibleText = displayText.replace(/<[^>]+>/g, '');
            const isLongDescription = visibleText.length > MAX_ELLIPSIS_LENGTH;
            
            // Check if there's meaningful text after the last link
            const lastLinkEnd = displayText.lastIndexOf('</a>');
            const hasLinks = lastLinkEnd !== -1;
            const textAfterLink = hasLinks ? displayText.substring(lastLinkEnd + 4).replace(/<[^>]+>/g, '').trim() : '';
            const linkNearEnd = hasLinks && textAfterLink.length < 50; // Less than 50 chars after link
            
            if (!hasMultipleUrls && isLongDescription && !linkNearEnd) {
                // Truncate while preserving HTML structure (safe if sufficient text after link)
                displayText = this.truncateHtml(displayText, MAX_ELLIPSIS_LENGTH);
            } else if (hasMultipleUrls || linkNearEnd) {
                // Apply scrolling if multiple URLs OR if link is near the end
                style.maxHeight = '180px';
                style.overflowY = 'auto';
                style.overflowX = 'hidden';
                style.scrollbarWidth = 'thin';
            }
        }

        const renderedContent = htmlToReact(displayText);
        
        if (safeHref) {
            return (
                <a
                    key={key}
                    style={style}
                    id={id}
                    className={fullClassName}
                    href={safeHref}
                    target={smartTarget}
                    rel={smartTarget === '_blank' ? 'noopener noreferrer' : undefined}>
                    {renderedContent}
                </a>
            );
        }

        return (
            <div
                key={key}
                style={style}
                id={id}
                className={fullClassName}>
                {renderedContent}
            </div>
        );
    }

    // Helper function to truncate HTML while preserving tags
    truncateHtml(html, maxLength) {
        let visibleLength = 0;
        let result = '';
        const openTags = [];
        
        // Parse HTML character by character
        for (let i = 0; i < html.length; i++) {
            if (html[i] === '<') {
                // Start of a tag
                const tagEnd = html.indexOf('>', i);
                if (tagEnd === -1) break;
                
                const tag = html.substring(i, tagEnd + 1);
                result += tag;
                
                // Track opening/closing tags
                if (!tag.startsWith('</') && !tag.endsWith('/>')) {
                    const tagName = tag.match(/<(\w+)/);
                    if (tagName) openTags.push(tagName[1]);
                } else if (tag.startsWith('</')) {
                    openTags.pop();
                }
                
                i = tagEnd;
            } else {
                // Regular character
                if (visibleLength >= maxLength) break;
                result += html[i];
                visibleLength++;
            }
        }
        
        // Close any remaining open tags
        while (openTags.length > 0) {
            result += `</${openTags.pop()}>`;
        }
        
        return result.trim() + "…";
    }

    linkifyText(text) {
        // Check for markdown-style links at the end of text (within last 100 chars)
        const markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g;
        const markdownMatches = [...text.matchAll(markdownLinkPattern)];
        
        // If markdown link is near the end, extract it and show as bullet
        if (markdownMatches.length > 0) {
            const lastMatch = markdownMatches[markdownMatches.length - 1];
            const matchPosition = lastMatch.index;
            const textAfterLink = text.substring(matchPosition + lastMatch[0].length).trim();
            
            // If link is near end (less than 20 chars after it), show as bullet list
            if (textAfterLink.length < 20) {
                const textBeforeLink = text.substring(0, matchPosition).trim();
                const linkText = lastMatch[1];
                const url = lastMatch[2];
                
                const listItem = `<li>
                    <a href="${url}" target="_blank" rel="noopener noreferrer" style="display: inline !important; visibility: visible !important; color: #0d6efd; text-decoration: underline;">${linkText}</a>
                </li>`;
                
                return `${textBeforeLink}<ul class="url-list">${listItem}</ul>`;
            }
        }
        
        // Otherwise, handle markdown-style links inline
        text = text.replace(markdownLinkPattern, (match, linkText, url) => {
            return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="display: inline !important; visibility: visible !important; color: #0d6efd; text-decoration: underline;">${linkText}</a>`;
        });
        
        const urlPattern = /(https?:\/\/\S+?)(?:\s+-\s+([^\n]+?))?(?=\s+https?:\/\/|\s*$)/g;
        let matches = [...text.matchAll(urlPattern)];
        
        if (matches.length >= 2) {
            const beforeFirstUrl = text.substring(0, text.indexOf(matches[0][0])).trim();
            
            const listItems = matches.map((match, index) => {
                let url = match[1].trim();
                let hoverText = match[2] ? match[2].trim() : ''; // hover text is optional (can be undefined)
                
                if (hoverText) {
                    hoverText = hoverText.replace(/\.\s*$/, '');
                }
                
                const displayText = this.getUrlDisplayText(url, index + 1);
                const infoIcon = hoverText 
                    ? `<i class="atm-icon info-circle" title="${hoverText}" style="margin-left: 5px; cursor: help;"></i>`
                    : '';
                
                return `<li>
                    <a href="${url}" target="_blank" rel="noopener noreferrer" style="display: inline !important; visibility: visible !important; color: #0d6efd; text-decoration: underline;">${displayText}</a>
                    ${infoIcon}
                </li>`;
            }).join('');
            
            return beforeFirstUrl 
                ? `${beforeFirstUrl}<ul class="url-list">${listItems}</ul>`
                : `<ul class="url-list">${listItems}</ul>`;
        }
        
        // Handle existing list format
        if (text.includes('<ul>') || text.includes('<li>')) {
            return text.replace(/<li>(.*?)<\/li>/g, (match, content) => {
                const linkedContent = content.replace(/(https?:\/\/[^\s<]+)/g, (url) => {
                    const displayText = this.getUrlDisplayText(url);
                    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="display: inline !important; visibility: visible !important; color: #0d6efd; text-decoration: underline;">${displayText}</a>`;
                });
                return `<li>${linkedContent}</li>`;
            });
        }
        
        // Single URL case - wrap in bullet list with optional hover text
        const singleUrlMatch = text.match(/(https?:\/\/\S+?)(?:\s+-\s+([^\n]*?))?$/);
        if (singleUrlMatch) {
            const beforeUrl = text.substring(0, text.indexOf(singleUrlMatch[0])).trim();
            const url = singleUrlMatch[1].trim();
            let hoverText = singleUrlMatch[2] ? singleUrlMatch[2].trim() : '';
            
            if (hoverText) {
                hoverText = hoverText.replace(/\.\s*$/, '');
            }
            
            const displayText = this.getUrlDisplayText(url);
            
            const infoIcon = hoverText 
                ? `<i class="atm-icon info-circle" title="${hoverText}" style="margin-left: 5px; cursor: help;"></i>`
                : '';
            
            const listItem = `<li>
                <a href="${url}" target="_blank" rel="noopener noreferrer" style="display: inline !important; visibility: visible !important; color: #0d6efd; text-decoration: underline;">${displayText}</a>
                ${infoIcon}
            </li>`;
            
            return beforeUrl 
                ? `${beforeUrl}<ul class="url-list">${listItem}</ul>`
                : `<ul class="url-list">${listItem}</ul>`;
        }
        
        return text;
    }

    // Helper function to generate display text for URLs
    getUrlDisplayText(url, index = null) {
        try {
            const urlLower = url.toLowerCase();
            
            // Check for specific keywords in URL
            if (urlLower.includes('uat-api')) {
                return 'UAT';
            }
            if (urlLower.includes('poe-api')) {
                return 'POE';
            }
            if (urlLower.includes('graphql-api') && !urlLower.includes('uat') && !urlLower.includes('poe')) {
                return 'PROD';
            }
            if (urlLower.includes('api-management-portal')) {
                return 'API Developer Portal';
            }
            
            // Simple fallback: use hostname
            const urlObj = new URL(url);
            return urlObj.hostname.replace('www.', '').replace('.cisco.com', '');
            
        } catch (e) {
            console.warn('Failed to parse URL:', url, e);
            return 'Link';
        }
    }

    getLinkTarget(href, target) {
        if (target) {
            return target;
        }

        return (href || '').indexOf('http') == 0 ? '_blank' : null;
    }
    renderImage(data, classNamePrefix = '') {
        if (!(data && data.src)) {
            return;
        }

        const loadingProp = data.loading ? { loading: data.loading } : {};
        const img = <img src={data.src} className={classNamePrefix + '-icon'} style={data.style} {...loadingProp} />

        const { src, href, target, id, className } = data;
        const classNameFixedPart = classNamePrefix + '-icon-container';
        const fullClassName = (`${classNameFixedPart} ${className}`).trim();

        if (data.href) {
            return (
                <a id={id} className={fullClassName} href={data.href} target={data.target} rel={data.target === '_blank' ? 'noopener noreferrer' : undefined}>
                    {img}
                </a>
            );
        }

        return (
            <div id={id} className={fullClassName}>
                {img}
            </div>
        );
    }
    rendeCardItems(items) {
        if (!items || items.items.length == 0) {
            return null;
        }

        const list = items.items.map((item, idx) => {
            var text = this.renderText(item, 'pubhub-feature-section-card-item');
            return (
                <li key={idx}>
                    {text}
                </li>
            );
        });

        const className = ('pubhub-feature-section-card-items ' + items.className).trim();
        return (
            <ul className={className} style={items.style}>
                {list}
            </ul>
        );
    }
    renderCardActionFlexGap(action) {
        if (action && (action.className.includes('type-button') || action.className.includes('sticky-bottom'))) {
            return (
                <div className='pubhub-feature-section-card-flex-gap'></div>
            );
        }

        return null;
    }
    getCardColumnClassName(card) {
        const columns = (card.columns || '3') + '';
        if (columns == 4) {
            return 'col-xl-3 col-lg-4 col-md-6';
        }
        if (columns == 3) {
            return 'col-lg-4 col-md-6';
        }
        if (columns == 2) {
            return 'col-lg-6';
        }

        return '';
    }
}